import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  CssBaseline,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmailIcon from '@mui/icons-material/Email';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ListAltIcon from '@mui/icons-material/ListAlt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HotelIcon from '@mui/icons-material/Hotel';
import GroupIcon from '@mui/icons-material/Group';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../store/authSlice';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';

const drawerWidth = 260;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const MainContent = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface MenuOption {
  title: string;
  icon: React.ReactNode;
  path: string;
  children?: MenuOption[];
}

const menuOptions: MenuOption[] = [
  { title: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  // { title: 'Analytics', icon: <BarChartIcon />, path: '/analytics' },
  { title: 'Agents', icon: <PeopleIcon />, path: '/agents' },
  // { 
  //   title: 'Customer Groups', 
  //   icon: <GroupIcon />, 
  //   path: '/groups',
  //   children: [
  //     { title: 'All Groups', icon: <ListAltIcon />, path: '/groups' },
  //     { title: 'Create Group', icon: <AddIcon />, path: '/groups/create' }
  //   ]
  // },
  { 
    title: 'Campaigns', 
    icon: <CampaignIcon />, 
    path: '/campaigns',
    children: [
      { title: 'All Campaigns', icon: <ListAltIcon />, path: '/campaigns' },
      { title: 'Create Campaign', icon: <AddIcon />, path: '/campaigns/create' },
      { title: 'Create WhatsApp campaign', icon: <AddIcon />, path: '/campaigns/create-whatsapp' }
    ]
  },
  { title: 'Email Templates', icon: <EmailIcon />, path: '/email-templates' },
  { title: 'WhatsApp Templates', icon: <WhatsAppIcon />, path: '/whatsapp-templates' },
  { title: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' },
  // { title: 'Property Recommendations', icon: <HotelIcon />, path: '/property-recommendations' },
  { title: 'User Management', icon: <ManageAccountsIcon />, path: '/users' },
  // { title: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAppSelector(state => state.auth);
  
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  
  // Get user display information
  const userDisplayName = user?.first_name || user?.username || 'User';
  const userRole = user?.profile?.role === 'admin' ? 'Admin' : 'Unit Manager';
  const userInitial = (user?.first_name || user?.username || 'U')[0].toUpperCase();
  
  // Debug: Log user role
  React.useEffect(() => {
    console.log('Current user:', user);
    console.log('User role:', user?.profile?.role);
  }, [user]);
  
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setOpen(false);
    }
  };
  
  const handleToggleSubmenu = (title: string) => {
    setOpenSubmenu(prevOpen => (prevOpen === title ? null : title));
  };

  // Filter menu options based on user role
  const filteredMenuOptions = React.useMemo(() => {
    const filtered = menuOptions.filter(option => {
      // Hide "User Management" for unit managers
      if (option.title === 'User Management') {
        const isUnitManager = user?.profile?.role === 'unit_manager';
        console.log(`Filtering "User Management": isUnitManager=${isUnitManager}, role=${user?.profile?.role}`);
        if (isUnitManager) {
          return false; // Hide for unit managers
        }
      }
      return true;
    });
    console.log('Filtered menu options:', filtered.map(o => o.title));
    return filtered;
  }, [user]);

  const menuId = 'primary-account-menu';
  const isMenuOpen = Boolean(anchorEl);

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id={menuId}
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
          mt: 1.5,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>Profile</MenuItem>
      <MenuItem onClick={() => { handleMenuClose(); navigate('/account'); }}>My account</MenuItem>
      <MenuItem onClick={handleLogout}>Logout</MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} color="default">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ABAD CRM Admin
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Notifications">
              
              <IconButton size="large" color="inherit">
                <NotificationsIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Account settings">
              <IconButton
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>{userInitial}</Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMenu}
      
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        <DrawerHeader sx={{ 
          justifyContent: 'space-between', 
          padding: theme.spacing(2),
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src="/abad.png" alt="Zovotel Logo" style={{ height: 128 }} />
            <Typography variant="h6" color="primary" fontWeight="bold">
              
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>{userInitial}</Avatar>
            <Box>
              <Typography variant="subtitle2">{userDisplayName}</Typography>
              <Typography variant="body2" color="text.secondary">{userRole}</Typography>
              {user?.profile?.role === 'unit_manager' && user?.profile?.properties && user.profile.properties.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {user.profile.properties.map(p => p.name).join(', ')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        
        <Divider />
        
        <List>
          {filteredMenuOptions.map((option) => (
            <React.Fragment key={option.title}>
              {option.children ? (
                // Menu with submenu
                <>
                  <ListItem disablePadding>
                    <ListItemButton 
                      onClick={() => handleToggleSubmenu(option.title)}
                      selected={window.location.pathname.startsWith(option.path)}
                    >
                      <ListItemIcon>
                        {option.icon}
                      </ListItemIcon>
                      <ListItemText primary={option.title} />
                      {openSubmenu === option.title ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={openSubmenu === option.title} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {option.children.map((childOption) => (
                        <ListItem key={childOption.title} disablePadding>
                          <ListItemButton 
                            onClick={() => handleNavigation(childOption.path)}
                            selected={window.location.pathname === childOption.path}
                            sx={{ pl: 4 }}
                          >
                            <ListItemIcon>
                              {childOption.icon}
                            </ListItemIcon>
                            <ListItemText primary={childOption.title} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                // Regular menu item
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleNavigation(option.path)}
                    selected={window.location.pathname === option.path}
                  >
                    <ListItemIcon>
                      {option.icon}
                    </ListItemIcon>
                    <ListItemText primary={option.title} />
                  </ListItemButton>
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
        
        <Divider />
        
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      
      <MainContent open={open}>
        <DrawerHeader />
        {children}
      </MainContent>
    </Box>
  );
};

export default DashboardLayout; 